/**
 * Removing a filter, and the filters that depended on it.
 *
 * A criterion can require another to be set first. Dropping the prerequisite without dropping what
 * relied on it leaves a filter whose condition no longer holds, still narrowing the table. Only
 * the session activities table did this cleanup before; it belongs wherever a filter is removed.
 *
 * @param {string} filterKey
 * @param {Record<string, { filterDependency?: { value: string } }>} filters
 * @returns {Record<string, object>} A new map; the caller's is left as it was.
 */
export default function removeFilter(filterKey, filters = {}) {
  /*
   * Widened until nothing new falls in, rather than one pass: a filter depending on a dependent is
   * left just as stranded as a direct one. Nothing declares a chain today, but the shape allows any
   * column to name any other.
   */
  const removed = new Set([filterKey]);
  let countBefore = 0;

  while (removed.size !== countBefore) {
    countBefore = removed.size;
    Object.entries(filters).forEach(([key, filter]) => {
      if (removed.has(filter?.filterDependency?.value)) {
        removed.add(key);
      }
    });
  }

  return Object.fromEntries(
    Object.entries(filters).filter(([key]) => !removed.has(key)),
  );
}

/**
 * Removing one statement from a filter, for a chip that stands for a single value.
 *
 * A match-all filter is stored as one statement per value and shown as one chip per statement, so
 * the cross on a chip has to reach that value alone. Taking the last one leaves no filter, which is
 * a removal proper and carries its dependents with it.
 *
 * @param {string} filterKey
 * @param {number} statementIndex
 * @param {Record<string, { filterStatements?: object[] }>} filters
 * @returns {Record<string, object>} A new map; the caller's is left as it was.
 */
export function removeFilterStatement(filterKey, statementIndex, filters = {}) {
  const remaining = (filters[filterKey]?.filterStatements ?? [])
    .filter((statement, index) => index !== statementIndex);

  if (remaining.length === 0) {
    return removeFilter(filterKey, filters);
  }

  return {
    ...filters,
    [filterKey]: { ...filters[filterKey], filterStatements: remaining },
  };
}
